/**
 * Faculty directory API (issue #11) — typed wrapper over `GET/POST /faculty`
 * and `PATCH /faculty/{id}`. Each row's `courses` array is the instructor's
 * COMPLETE per-term teaching load (server-computed via a SECURITY DEFINER
 * helper so it is never truncated by the caller's own course-visibility RLS —
 * see `backend/app/routers/faculty.py`). Every row is named by its person
 * (ADR 0034); the login account's name is never part of a row.
 */
import { api } from "@/lib/api/client";
import { commitOptions } from "@/lib/api/commitSeams";
import { comparePersonNames } from "@/lib/person-name";

export interface FacultyCourseLoad {
  course_id: string;
  course_title: string;
  crn: string | null;
  semester_id: string | null;
  semester_name: string | null;
  status: string;
  assignment_kind?: "primary" | "co_instructor";
}

/** An active staff account waiting for a faculty profile. */
export interface EligibleFacultyAccount {
  user_id: string;
  role: "admin" | "instructor";
  is_active: true;
  is_super_admin: boolean;
  /** The person's full name; null until the registrar names them. */
  display_name: string | null;
  /** The account's sign-in address, else the person's email. */
  email: string | null;
}

export interface FacultyRemovalBlocker {
  type: "course_assignment";
  id: string;
  label: string;
  status: string;
  semester_name: string | null;
}

export interface FacultyRemovalPreview {
  faculty_id: string;
  user_id: string | null;
  can_remove: boolean;
  blockers: FacultyRemovalBlocker[];
}

export interface FacultyProfile {
  id: string;
  archived_at?: string | null;
  person_id?: string;
  user_id: string | null;
  /** The person's full name; null until the registrar names them. */
  display_name: string | null;
  account_link_state?: "not_linked" | "linked";
  portal_access?: "none" | "active" | "inactive";
  honorific: string | null;
  title_role: string | null;
  specialization: string | null;
  training_chain: string | null;
  email: string | null;
  created_at: string;
  /** The profile's version: a save of a teaching fact is checked against it. */
  updated_at: string;
  /** The person's version, where the read carries it: a save of a person fact is checked against it. */
  person_updated_at?: string;
  account_role: "admin" | "instructor" | "student" | null;
  account_is_active: boolean | null;
  courses: FacultyCourseLoad[];
}

export type FacultyProfileWriteResult = Omit<
  FacultyProfile,
  | "account_role"
  | "account_is_active"
  | "courses"
> & {
  /** The person the profile is a role of (ADR 0034): the address of their record. */
  person_id: string;
};

interface FacultyProfileFields {
  honorific?: string | null;
  title_role?: string | null;
  specialization?: string | null;
  training_chain?: string | null;
  email?: string | null;
}

export type CreateFacultyProfile = FacultyProfileFields & (
  | { account_user_id: string; display_name?: never }
  | { display_name: string; account_user_id?: never }
);

export type PatchFacultyProfile = Partial<FacultyProfileFields> & {
  display_name?: string;
};

/**
 * The versions a profile save hands back, as read. The server checks each only
 * when the save writes that row (ADR 0034), and refuses a stale one with 409
 * and the profile as it now stands. Absent means no check.
 */
export interface ExpectedFacultyVersions {
  expected_updated_at?: string;
  expected_person_updated_at?: string;
}

export interface RemoveFacultyProfileResult {
  faculty_id: string;
  user_id: string | null;
  deleted: true;
  removed: ["faculty_profile"];
}

export const facultyApi = {
  /** The directory in name order, the way every list and picker shows it. */
  list: async (semesterId?: string) => {
    const rows = await api.get<FacultyProfile[]>(`/faculty${semesterId ? `?semester_id=${semesterId}` : ""}`);
    return [...rows].sort((a, b) => comparePersonNames(a.display_name, b.display_name));
  },
  /** Every write takes a commit key where the caller has one: Check reads whether it landed (W1). */
  create: (body: CreateFacultyProfile, key?: string) =>
    api.post<FacultyProfileWriteResult>("/faculty", body, key ? commitOptions(key) : undefined),
  eligibleAccounts: () => api.get<EligibleFacultyAccount[]>("/faculty/eligible-accounts"),
  patch: (id: string, body: PatchFacultyProfile & ExpectedFacultyVersions, key?: string) =>
    api.patch<FacultyProfileWriteResult>(`/faculty/${id}`, body, key ? commitOptions(key) : undefined),
  removalPreview: (id: string) =>
    api.get<FacultyRemovalPreview>(`/faculty/${id}/removal-preview`),
  remove: (id: string, key?: string) =>
    api.delete<RemoveFacultyProfileResult>(`/faculty/${id}`, undefined, key ? commitOptions(key) : undefined),
};
